import clientEnv from '../../env';
/**
 * Validation rules for different data types
 */
export const validationRules = {
    // Lesson validation rules
    lessons: [
        {
            name: 'lesson_has_title',
            validate: (lesson) => {
                // Debug logging in development
                if (clientEnv.NODE_ENV === 'development' && !lesson.title) {
                }
                return lesson && lesson.title && typeof lesson.title === 'string' && lesson.title.trim().length > 0;
            },
            message: 'Lesson must have a title',
            severity: 'error'
        },
        {
            name: 'lesson_has_subject',
            validate: (lesson) => {
                // Enhanced debug logging in development
                if (clientEnv.NODE_ENV === 'development') {
                }
                // Allow null subject_id since database schema allows it (ON DELETE SET NULL)
                // This is common for lesson templates, drafts, or lessons that inherit from classroom
                // Only validate that if subject_id is provided, it's a valid non-empty string
                return lesson && (lesson.subject_id === null || lesson.subject_id === undefined ||
                    (typeof lesson.subject_id === 'string' && lesson.subject_id.trim().length > 0));
            },
            message: 'Lesson subject_id must be valid if provided (null is allowed for templates/drafts)',
            severity: 'warning' // Changed from error to warning since null is valid
        },
        {
            name: 'lesson_has_valid_difficulty',
            validate: (lesson) => {
                // Debug logging in development
                if (clientEnv.NODE_ENV === 'development' && lesson.difficulty && !['beginner', 'intermediate', 'advanced'].includes(lesson.difficulty)) {
                }
                return !lesson.difficulty || ['beginner', 'intermediate', 'advanced'].includes(lesson.difficulty);
            },
            message: 'Lesson must have a valid difficulty level',
            severity: 'warning'
        },
        {
            name: 'lesson_has_valid_duration',
            validate: (lesson) => {
                // Debug logging in development
                if (clientEnv.NODE_ENV === 'development' && lesson.duration && (lesson.duration <= 0 || lesson.duration > 480)) {
                }
                return !lesson.duration || (lesson.duration > 0 && lesson.duration <= 480);
            },
            message: 'Lesson duration must be between 1 and 480 minutes',
            severity: 'warning'
        }
    ],
    // Note validation rules
    notes: [
        {
            name: 'note_has_title',
            validate: (note) => note && note.title && note.title.trim().length > 0,
            message: 'Note must have a title',
            severity: 'error'
        },
        {
            name: 'note_has_student_id',
            validate: (note) => note && note.student_id && note.student_id.trim().length > 0,
            message: 'Note must have a student ID',
            severity: 'error'
        },
        {
            name: 'note_has_valid_content',
            validate: (note) => note && note.content !== undefined && note.content !== null,
            message: 'Note must have content',
            severity: 'warning'
        },
        {
            name: 'note_has_valid_metadata',
            validate: (note) => note && note.metadata && typeof note.metadata === 'object',
            message: 'Note must have valid metadata',
            severity: 'warning'
        }
    ],
    // User validation rules
    user: [
        {
            name: 'user_has_id',
            validate: (user) => user && user.id && user.id.trim().length > 0,
            message: 'User must have an ID',
            severity: 'error'
        },
        {
            name: 'user_has_email',
            validate: (user) => user && user.email && user.email.includes('@'),
            message: 'User must have a valid email',
            severity: 'error'
        },
        {
            name: 'user_has_role',
            validate: (user) => user && user.role && ['student', 'tutor', 'admin'].includes(user.role),
            message: 'User must have a valid role',
            severity: 'error'
        }
    ],
    // Schedule validation rules
    schedule: [
        {
            name: 'schedule_has_valid_structure',
            validate: (schedule) => {
                // Check if schedule has the expected structure
                return schedule && (schedule.tutor_availability !== undefined ||
                    schedule.tutor_schedule !== undefined ||
                    schedule.booked_lessons !== undefined);
            },
            message: 'Schedule must have valid structure (tutor_availability, tutor_schedule, or booked_lessons)',
            severity: 'error'
        },
        {
            name: 'schedule_has_valid_tutor_availability',
            validate: (schedule) => {
                if (!schedule.tutor_availability)
                    return true; // Optional field
                const availability = schedule.tutor_availability;
                return availability &&
                    availability.tutor_id &&
                    availability.tutor_id.trim().length > 0 &&
                    availability.weekly_schedule &&
                    typeof availability.weekly_schedule === 'object';
            },
            message: 'Tutor availability must have valid tutor_id and weekly_schedule',
            severity: 'warning'
        },
        {
            name: 'schedule_has_valid_lessons',
            validate: (schedule) => {
                if (!schedule.tutor_schedule && !schedule.booked_lessons)
                    return true; // Both optional
                const lessons = schedule.tutor_schedule || schedule.booked_lessons;
                return Array.isArray(lessons);
            },
            message: 'Schedule lessons must be an array',
            severity: 'warning'
        },
        {
            name: 'schedule_has_valid_state_properties',
            validate: (schedule) => {
                // Check that required state properties exist
                return schedule &&
                    typeof schedule.is_loading === 'boolean' &&
                    typeof schedule.status === 'string' &&
                    (schedule.error === null || typeof schedule.error === 'string');
            },
            message: 'Schedule must have valid state properties (is_loading, status, error)',
            severity: 'warning'
        }
    ]
};
/**
 * Validate data against a set of rules
 */
export const validateData = (data, rules) => {
    const errors = [];
    const warnings = [];
    rules.forEach(rule => {
        try {
            const isValid = rule.validate(data);
            if (!isValid) {
                if (rule.severity === 'error') {
                    errors.push(`${rule.name}: ${rule.message}`);
                }
                else {
                    warnings.push(`${rule.name}: ${rule.message}`);
                }
            }
        }
        catch (error) {
            console.error(`Validation rule ${rule.name} failed:`, error);
            errors.push(`${rule.name}: Validation rule execution failed`);
        }
    });
    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
};
/**
 * Validate Redux state for data consistency
 */
export const validateReduxState = (state) => {
    const errors = [];
    const warnings = [];
    // Validate lessons
    if (state.lessons?.items && Array.isArray(state.lessons.items)) {
        state.lessons.items.forEach((lesson, index) => {
            const lessonValidation = validateData(lesson, validationRules.lessons);
            errors.push(...lessonValidation.errors.map(e => `Lessons[${index}]: ${e}`));
            warnings.push(...lessonValidation.warnings.map(w => `Lessons[${index}]: ${w}`));
        });
    }
    // Validate notes
    if (state.notes?.items && Array.isArray(state.notes.items)) {
        state.notes.items.forEach((note, index) => {
            const noteValidation = validateData(note, validationRules.notes);
            errors.push(...noteValidation.errors.map(e => `Notes[${index}]: ${e}`));
            warnings.push(...noteValidation.warnings.map(w => `Notes[${index}]: ${w}`));
        });
    }
    // Validate user
    if (state.auth?.user) {
        const userValidation = validateData(state.auth.user, validationRules.user);
        errors.push(...userValidation.errors.map(e => `User: ${e}`));
        warnings.push(...userValidation.warnings.map(w => `User: ${w}`));
    }
    // Validate schedule
    if (state.schedule) {
        const scheduleValidation = validateData(state.schedule, validationRules.schedule);
        errors.push(...scheduleValidation.errors.map(e => `Schedule: ${e}`));
        warnings.push(...scheduleValidation.warnings.map(w => `Schedule: ${w}`));
    }
    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
};
/**
 * Validate specific data type
 */
export const validateDataType = (data, dataType) => {
    const rules = validationRules[dataType];
    if (!rules) {
        console.warn(`No validation rules found for data type: ${dataType}`);
        return { isValid: true, errors: [], warnings: [] };
    }
    // Add debug logging for schedule validation (only in development)
    if (dataType === 'schedule' && clientEnv.NODE_ENV === 'development') {
    }
    return validateData(data, rules);
};
/**
 * Redux middleware for data validation
 */
export const dataValidationMiddleware = (store) => (next) => (action) => {
    const prevState = store.getState();
    const result = next(action);
    const nextState = store.getState();
    // Test validation rules in development (only once)
    if (clientEnv.NODE_ENV === 'development' && action?.type?.includes('schedule') && !window.__validationTested) {
        testScheduleValidation();
        window.__validationTested = true;
    }
    // Validate data after successful operations
    if (action?.type && typeof action.type === 'string' && action.type.endsWith('/fulfilled')) {
        try {
            // Validate specific data types based on action - only for actual lesson actions
            if (action.type.includes('lessons') && !action.type.includes('quiz')) {
                // Enhanced debug logging to see actual lesson data structure
                if (clientEnv.NODE_ENV === 'development' && nextState.lessons?.items) {
                }
                // Validate each lesson individually if it's an array - only for actual lesson data
                if (nextState.lessons?.items && Array.isArray(nextState.lessons.items)) {
                    nextState.lessons.items.forEach((lesson, index) => {
                        // Skip validation for lessons with null subject_id (they might be templates or drafts)
                        if (lesson.subject_id === null || lesson.subject_id === undefined) {
                            return;
                        }
                        const lessonValidation = validateDataType(lesson, 'lessons');
                        if (!lessonValidation.isValid) {
                            console.warn(`📊 Lesson data validation failed for lesson[${index}]:`, lessonValidation);
                        }
                    });
                }
                else if (nextState.lessons?.items) {
                    // Single lesson validation - skip if no subject_id
                    const lesson = Array.isArray(nextState.lessons.items) ? nextState.lessons.items[0] : nextState.lessons.items;
                    if (lesson && (lesson.subject_id === null || lesson.subject_id === undefined)) {
                    }
                    else if (lesson) {
                        const lessonValidation = validateDataType(lesson, 'lessons');
                        if (!lessonValidation.isValid) {
                            console.warn('📊 Lesson data validation failed:', lessonValidation);
                        }
                    }
                }
            }
            if (action.type.includes('notes')) {
                // Validate each note in the array individually
                if (nextState.notes?.items && Array.isArray(nextState.notes.items)) {
                    nextState.notes.items.forEach((note, index) => {
                        const noteValidation = validateDataType(note, 'notes');
                        if (!noteValidation.isValid) {
                            console.warn(`📊 Note data validation failed for note[${index}]:`, noteValidation);
                        }
                    });
                }
            }
            if (action.type.includes('user') || action.type.includes('auth')) {
                // Only validate user if it exists and is not null
                if (nextState.auth?.user) {
                    const userValidation = validateDataType(nextState.auth.user, 'user');
                    if (!userValidation.isValid) {
                        console.warn('📊 User data validation failed:', userValidation);
                    }
                }
            }
            if (action.type.includes('schedule')) {
                const scheduleValidation = validateDataType(nextState.schedule, 'schedule');
                if (!scheduleValidation.isValid) {
                    console.warn('📊 Schedule data validation failed:', scheduleValidation);
                }
            }
            // Comprehensive state validation (only in development)
            if (clientEnv.NODE_ENV === 'development') {
                const stateValidation = validateReduxState(nextState);
                if (!stateValidation.isValid) {
                    console.group('📊 Redux State Validation Issues');
                    if (stateValidation.errors.length > 0) {
                        console.error('❌ Errors:', stateValidation.errors);
                    }
                    if (stateValidation.warnings.length > 0) {
                        console.warn('⚠️ Warnings:', stateValidation.warnings);
                    }
                    console.groupEnd();
                }
            }
        }
        catch (validationError) {
            console.error('📊 Data validation middleware error:', validationError);
        }
    }
    return result;
};
/**
 * Hook for components to validate data
 */
export const useDataValidation = () => {
    const validateDataInComponent = (data, dataType) => {
        return validateDataType(data, dataType);
    };
    const validateState = (state) => {
        return validateReduxState(state);
    };
    return {
        validateData: validateDataInComponent,
        validateState,
        validationRules
    };
};
/**
 * Utility function to log validation results
 */
export const logValidationResults = (validation, context) => {
    if (!validation.isValid) {
        console.group(`📊 Data Validation - ${context}`);
        if (validation.errors.length > 0) {
            console.error('❌ Validation Errors:', validation.errors);
        }
        if (validation.warnings.length > 0) {
            console.warn('⚠️ Validation Warnings:', validation.warnings);
        }
        console.groupEnd();
    }
};
/**
 * Custom validation rule builder
 */
export const createValidationRule = (name, validate, message, severity = 'error') => ({
    name,
    validate,
    message,
    severity
});
/**
 * Composite validation rule
 */
export const createCompositeValidationRule = (name, rules, message, severity = 'error') => ({
    name,
    validate: (data) => rules.every(rule => rule.validate(data)),
    message,
    severity
});
/**
 * Test function to verify validation rules work correctly
 */
export const testScheduleValidation = () => {
    // Test with initial state (should pass)
    const initialState = {
        tutor_availability: null,
        tutor_schedule: [],
        booked_lessons: [],
        is_loading: false,
        error: null,
        status: 'idle',
    };
    const initialValidation = validateDataType(initialState, 'schedule');
    // Test with populated state (should pass)
    const populatedState = {
        tutor_availability: {
            tutor_id: 'test-tutor-id',
            weekly_schedule: {
                monday: [{ start_time: '09:00', end_time: '10:00' }]
            },
            date_overrides: [],
            timezone: 'UTC',
            lesson_duration: 60,
            break_between_lessons: 15,
            is_active: true,
            available_slots: []
        },
        tutor_schedule: [],
        booked_lessons: [],
        is_loading: false,
        error: null,
        status: 'idle',
    };
    const populatedValidation = validateDataType(populatedState, 'schedule');
    // Test with invalid state (should fail)
    const invalidState = {
        tutor_availability: {
            tutor_id: '', // Invalid: empty tutor_id
            weekly_schedule: null, // Invalid: null weekly_schedule
        },
        tutor_schedule: 'not-an-array', // Invalid: not an array
        booked_lessons: [],
        is_loading: false,
        error: null,
        status: 'idle',
    };
    const invalidValidation = validateDataType(invalidState, 'schedule');
};
// Test function is already exported above 
